import { next } from '@ember/runloop';
import { service } from '@ember/service';
import { cached, tracked } from '@glimmer/tracking';

import type { APIData, Coredata, DateRangeInterface, DSSetup, Service } from 'webapp-types';
import { GranulationTypeId, TimeUnit } from 'webapp/enums/enums';
import { copyObject, equal, isAlive } from 'webapp/utils/common';

import type DataSelect from 'webapp/models-custom/data-select';
import DateRange from 'webapp/models-custom/date-range';
import DsCompare from 'webapp/models-custom/ds-compare';
import Granulation from 'webapp/models-custom/granulation';
import type MetricLimitationCompare from 'webapp/models-custom/metric-limitation/compare';
import type MetricLimitationCumulative from 'webapp/models-custom/metric-limitation/cumulative';
import type MetricLimitationGranulation from 'webapp/models-custom/metric-limitation/granulation';
import MetricLimitationRange from 'webapp/models-custom/metric-limitation/range';
import RangeCategory from 'webapp/models-custom/range-category';
import Serviced from 'webapp/models-custom/serviced';

// Responsible for generation and updating of range and granularity lists with limitations applied
// `metrics` are optional (without them, limitations endpoint will return acc-wide limitations)
// `compare` is optional
// When switching to/from isNumber, granulation part on date-range is updated by metric-limitations.
// If date range is changed to something that does not support selected granularity, metric-limitations switches the granularity to first available. Same with cumulative.
// MetricLimitations always has at least one compareCM for easier component handling
// if the ms/ds has no compares chosen, that one existing is None

export interface CompareCategoryWithItems {
	id: number; // one of CompareCategory
	title: string;
	isSingleItem: boolean;
	isDatepicker: boolean;
	items: DsCompare[];
}

export interface MetricLimitationsConstructOptions {
	metrics?: (DSSetup.MetricData | null)[];
	isNumber?: boolean;
	dateRange?: DateRange;
	compareCMs?: DsCompare[];
	fetchLimitationsOnInit?: boolean;
	manualLimitations?: boolean;
	rawLimitations?: APIData.Metrics.RangeLimitations | null;
}

export default class MetricLimitations extends Serviced {
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('model-manager/metrics') declare mmMetricsService: Service.ModelManager.Metrics;

	@tracked fetchLimitationsOnInit = true; // if set to false, skip fetching limitations on init (will be called manually, e.g. when opening a DR dropdown)

	@tracked manualLimitations = false; // do not fetch or set limitations if this flag is active

	constructor(options: MetricLimitationsConstructOptions) {
		super();

		if (options.metrics) {
			this._metrics = this.filterMetrics(options.metrics); // to not trigger updateLimitations yet
		}
		if (options.isNumber) {
			this._isNumber = options.isNumber; // to not trigger updateGranulationFromIsNumber
		}
		if (options.dateRange) {
			this.dateRange = options.dateRange;
		}
		if (options.compareCMs) {
			this.compareCMs = options.compareCMs;
		}
		if (options.fetchLimitationsOnInit === false) {
			this.fetchLimitationsOnInit = false;
		}
		if (options.manualLimitations) {
			this.manualLimitations = options.manualLimitations;
		}
		if (options.rawLimitations) {
			this.rawLimitations = options.rawLimitations;
		}

		if (this.fetchLimitationsOnInit) {
			this.updateLimitations();
		}
	}

	@tracked _rawLimitations: APIData.Metrics.RangeLimitations | null | undefined = null; // loaded from API

	get rawLimitations(): APIData.Metrics.RangeLimitations | null | undefined {
		return this._rawLimitations;
	}

	set rawLimitations(value: APIData.Metrics.RangeLimitations | null | undefined) {
		this._rawLimitations = value;
		this.checkValidityForAll();
	}

	@tracked private _isNumber = false; // different limitations for Total granularity & Cumulative

	get isNumber(): boolean {
		return this._isNumber;
	}

	set isNumber(value: boolean) {
		this._isNumber = value;
		this.updateGranulationFromIsNumber();
		this.checkValidityForAll();
	}

	@tracked private _metrics: DSSetup.MetricData[] = [];

	get metrics(): DSSetup.MetricData[] {
		return this._metrics;
	}

	set metrics(value: (DSSetup.MetricData | null)[]) {
		const filteredValue = this.filterMetrics(value);
		if (!equal(this._metrics, filteredValue)) {
			this._metrics = filteredValue;
			this.updateLimitations();
		}
	}

	get metric(): DSSetup.MetricData | null {
		return this.metrics[0] || null;
	}

	private filterMetrics(value: (DSSetup.MetricData | null)[]): DSSetup.MetricData[] {
		return value.filter((metric) => !!metric);
	}

	// -------------- Selected range (when created from MS) -------------

	@tracked dateRange: DateRange | null = null; // set from outside

	@tracked compareCMs: DsCompare[] = []; // set from outside

	get compare(): DsCompare | null {
		return this.compareCMs[0] || null;
	}

	addCompare(compare: DsCompare): void {
		this.compareCMs.push(compare);
	}

	removeCompare(compare: DsCompare): void {
		this.compareCMs = this.compareCMs.filter((c) => c !== compare);
	}

	isDateRangeSupported(dateRange: DateRange): boolean {
		const limitationsForRange = this.getLimitationsForRange(dateRange.rangeTypeId),
			limitationsForGranulation = (limitationsForRange.childLimitations || []).find(
				(item) => item.id === dateRange.granulationTypeId,
			),
			limitationsForCumulative = limitationsForGranulation
				? (limitationsForGranulation.childLimitations || [])[0]
				: null;

		return (
			!limitationsForRange?.disabled &&
			!limitationsForGranulation?.disabled &&
			!limitationsForCumulative?.disabled
		);
	}

	@cached
	get limitationsForSelectedRange(): MetricLimitationRange | null {
		if (!this.dateRange) {
			return null;
		}

		return this.getLimitationsForRange(this.dateRange.rangeTypeId);
	}

	@cached
	get limitationForSelectedRangeAndGranulation(): MetricLimitationGranulation | null {
		next(this, () => this.checkValidityForGranulation());

		if (!this.dateRange) {
			return null;
		}

		const limitationForRange = this.limitationsForSelectedRange;
		if (!limitationForRange) {
			return null;
		}

		return (
			(limitationForRange.childLimitations || []).find(
				(item) => item.id === this.dateRange?.granulationTypeId,
			) || null
		);
	}

	@cached
	get limitationForCumulative(): MetricLimitationCumulative | null {
		next(this, () => this.checkValidityForCumulative());

		return this.limitationForSelectedRangeAndGranulation?.childLimitations[0] || null;
	}

	@cached
	get limitationsForCompareForSelectedRange(): MetricLimitationCompare[] {
		if (!this.dateRange) {
			return [];
		}

		next(this, () => this.checkValidityForCompares());

		// we need last x limitations to apply maxMultiplier to compare ranges
		// also, to match limitations
		const ranges = this.ranges,
			lastXLimitations = ranges.find((item) => item.isLastX)?.limitations || [],
			customLimitation = ranges
				.find((item) => item.isCustom)
				?.limitations.find((item) => item.id === 'CustomRange');

		return DsCompare.getLimitationsForDateRange(
			this.dateRange,
			lastXLimitations,
			customLimitation,
		);
	}

	private getLimitationForCompare(typeId: string): MetricLimitationCompare | null {
		const limitation = (this.limitationsForCompareForSelectedRange || []).find(
			(item) => item.id === typeId,
		);
		return limitation || null;
	}

	get isOnlyAllTimeEnabled(): boolean {
		// used for isScriptStatic logic
		return !this.ranges.some((range) => {
			return (range.isAllTime && range.isDisabled) || (!range.isAllTime && !range.isDisabled);
		});
	}

	// ------------- Selector (dropdown) models w/ limitations ------------

	_ranges: DateRange[] = [];

	@cached
	get ranges(): DateRange[] {
		if (!this._ranges.length) {
			this._ranges = DateRange.predefinedRanges.map((item) => item.createCopy());
		}

		const ranges = this._ranges;

		ranges.forEach((range) => {
			const limitations: MetricLimitationRange[] = [];
			range.rangeTypeIdVariations.forEach((rangeTypeId) => {
				limitations.push(this.getLimitationsForRange(rangeTypeId));
			});
			range.limitations = limitations;
		});

		return ranges;
	}

	@cached
	get granulations(): Granulation[] {
		const granulations = DateRange.predefinedGranulations
			.filter((item) => {
				return (
					(this.isNumber && item.unit === TimeUnit.TOTAL) ||
					(!this.isNumber && item.unit !== TimeUnit.TOTAL)
				);
			})
			.map((item) => new Granulation(copyObject(item)));

		const limitations = this.limitationsForSelectedRange?.childLimitations || [];

		granulations.forEach((granulation) => {
			// add correct limitations info to each granulation
			granulation.limitation = limitations.find((item) => item.id === granulation.id) || null;
		});

		return granulations;
	}

	_compares: DsCompare[] = [];

	@cached
	get compares(): DsCompare[] {
		if (!this._compares.length) {
			this._compares = DsCompare.predefinedCompares.map((item) => {
				const copy = item.createCopy();
				copy.customNoneText = this.compare?.customNoneText || null; // used for master compare to pass custom none text to all dropdown options
				return copy;
			});
		}

		const compares = this._compares;

		compares.forEach((item) => {
			if (item.isPrevious && this.dateRange) {
				item.updateFromDateRange(this.dateRange);
			}
		});

		const rangeCompareLimitations = this.limitationsForCompareForSelectedRange;

		compares.forEach((compare) => {
			const limitations: MetricLimitationCompare[] = [];
			compare.typeIdVariations.forEach((typeId) => {
				const limitation = rangeCompareLimitations.find((item) => item.id === typeId);
				if (limitation) {
					limitations.push(limitation);
				}
			});
			compare.limitations = limitations;
		});

		return compares;
	}

	// ---------- Creating limitation tree ----------

	getLimitationsForRange(rangeTypeId: string): MetricLimitationRange {
		const metricLimitations = this.rawLimitations?.[rangeTypeId] || null;

		return new MetricLimitationRange(
			rangeTypeId,
			metricLimitations,
			false,
			this.isNumber,
			() => {
				return this.getGranularityConstraints(rangeTypeId);
			},
		);
	}

	// -------- Switching unsupported ---------

	private getOptimalGranularityTypeId(): GranulationTypeId | '' {
		let granulationTypeId: GranulationTypeId | '' = '';

		if (this.isNumber) {
			granulationTypeId = GranulationTypeId.TOTAL;
		} else if (this.dateRange) {
			const list = this.dateRange.getOptimalGranulationsList();
			list.some((granId) => {
				const limitation = (this.limitationsForSelectedRange?.childLimitations || []).find(
					(item) => item.id === granId,
				);
				if (!limitation?.disabled) {
					granulationTypeId = granId;
					return true;
				}
				return false;
			});
		}

		return granulationTypeId;
	}

	// when switching to/from isNumber, granulation part on date-range should be updated
	private updateGranulationFromIsNumber(): void {
		if (!this.dateRange) {
			return;
		}

		let granulationTypeId: GranulationTypeId | null = null;

		if (this.isNumber) {
			if (!this.dateRange?.isGranulationTotal) {
				granulationTypeId = GranulationTypeId.TOTAL;
			}
		} else if (this.dateRange?.isGranulationTotal) {
			// switch to optimal granularity
			const optimal = this.getOptimalGranularityTypeId();
			if (optimal) {
				granulationTypeId = optimal;
			}
		}

		if (granulationTypeId) {
			this.dateRange.updateGranulationFromTypeId(granulationTypeId);
		}
	}

	// when selected granularity is disabled, switch to "optimal"
	private checkValidityForGranulation(): void {
		if (!this.dateRange) {
			return;
		}

		// if (!isAlive(this)) {
		// 	return;
		// }

		const limitation = this.limitationForSelectedRangeAndGranulation;

		if (limitation?.disabled) {
			// switch to first available granularity
			const granulationTypeId = this.getOptimalGranularityTypeId();

			if (granulationTypeId) {
				this.dateRange.updateGranulationFromTypeId(granulationTypeId);
			}
		}
	}

	selectOptimalValidGranulation(): void {
		const granulationTypeId = this.getOptimalGranularityTypeId();
		if (
			this.dateRange &&
			granulationTypeId &&
			this.dateRange.granulationTypeId !== granulationTypeId
		) {
			this.dateRange.updateGranulationFromTypeId(granulationTypeId);
		}
	}

	// when selected cumulative is not available, switch to first available
	private checkValidityForCumulative(): void {
		const limitation = this.limitationForCumulative;

		if (this.dateRange && limitation) {
			if (this.dateRange.granulationCumulative && !limitation.allowCumulative) {
				this.dateRange.granulationCumulative = false;
			} else if (!this.dateRange.granulationCumulative && !limitation.allowSeparate) {
				this.dateRange.granulationCumulative = true;
			}
		}
	}

	// when selected compare is not available, switch to first available
	private checkValidityForCompares(): void {
		const dateRange = this.dateRange;
		if (dateRange) {
			const switchToId =
				dateRange.isAllTime || dateRange.isSince
					? DsCompare.customNoneId
					: DsCompare.customPreviousId;

			this.compareCMs.forEach((compare) => {
				const limitation = this.getLimitationForCompare(compare.typeId);
				if (limitation?.disabled) {
					// on all time, switch to none
					// else switch to previous compare (that is always available)
					compare.updateFromTypeId(switchToId, dateRange);
				}
			});
		}
	}

	private checkValidityForAll() {
		this.checkValidityForGranulation();
		this.checkValidityForCumulative();
		this.checkValidityForCompares();
	}

	// ---------- Updating range (proxy) + compare -------
	updateDateRangeFromFormula(formula: DateRangeInterface.FormulaInterface): void {
		if (this.dateRange) {
			this.dateRange.updateFromFormula(formula);
			this.updateComparesFromDateRange();
			this.updateLimitationsForSelectedRangeMultiplier();
			this.checkValidityForAll();
		}
	}

	updateDateRangeFromOnlyRangeFormula(rangeFormula: DateRangeInterface.RangeInterface): void {
		if (this.dateRange) {
			this.dateRange.updateRange(rangeFormula);
			this.updateComparesFromDateRange();
			this.updateLimitationsForSelectedRangeMultiplier();
			this.checkValidityForCompares();
		}
	}

	// when timeUnit or multiplier changed on the dateRange, adjust compare for previous
	private updateComparesFromDateRange(): void {
		const dateRange = this.dateRange;
		if (dateRange) {
			this.compareCMs.forEach((compare) => {
				compare.updateFromDateRange(dateRange);
			});
		}
	}

	private updateLimitationsForSelectedRangeMultiplier(): void {
		if (this.dateRange?.isLastX) {
			const limitations = this.limitationsForSelectedRange;
			if (limitations) {
				limitations.chosenRangeMultiplier = this.dateRange.rangeMultiplier;
			}
		}
	}

	// ---------- Utility functions ----------

	_rangesByCategories: RangeCategory[] = [];

	@cached
	get rangesByCategories(): RangeCategory[] {
		if (!this._rangesByCategories.length) {
			this._rangesByCategories = DateRange.rangeCategories.map((category) => {
				return new RangeCategory(
					category.id,
					category.title,
					category.id === DateRange.Category.CUSTOM,
				);
			});
		}

		const categories = this._rangesByCategories;

		categories.forEach((category) => {
			const items = this.ranges.filter((item) => item.rangeCategoryId === category.id);
			category.isSingleItem = items.length === 1 && !items[0]!.hasExtraSettings;
			category.items = items;
		});

		return categories;
	}

	get comparesByCategories(): CompareCategoryWithItems[] {
		const categories: CompareCategoryWithItems[] = [];

		DsCompare.compareCategories.forEach((category) => {
			const items = this.compares.filter((item) => item.categoryId === category.id);
			categories.push({
				id: category.id,
				title: category.title,
				isSingleItem: items.length === 1 && !items[0]!.hasExtraSettings,
				isDatepicker: category.id === DsCompare.Category.CUSTOM,
				items,
			});
		});

		return categories;
	}

	get predefinedCompares(): DsCompare[] {
		return DsCompare.predefinedCompares;
	}

	updateLimitations(): void {
		let limitationsPromise;

		this._setupLimitationsDataAndObservers(); // only run once if not initialized yet

		if (this.manualLimitations) {
			limitationsPromise = Promise.resolve();
		} else {
			limitationsPromise = this.mmMetricsService
				.getMetricLimitations(this.metrics)
				.then(
					(limitations) => {
						// TESTDATA

						// no limitations whatsoever
						// limitations = {ranges:{}};

						// pricing limitations for this Quarter interval, and Daily granulation on Today To Hour
						// limitations = window.dbx_mock['POST /d/metrickeys/limitations - 1'];

						// legacy with constraints
						// limitations = window.dbx_mock['POST /d/metrickeys/limitations - 2'];

						if (isAlive(this) && !equal(this.rawLimitations, limitations.ranges)) {
							this.rawLimitations = limitations.ranges;
						}
					},
					() => {
						// skip on error
					},
				)
				.finally(() => {
					if (isAlive(this)) {
						this.limitationsLoaded = Promise.resolve();
					}
				});
		}

		this.limitationsLoaded = limitationsPromise;
	}

	changeMetric(metric: DSSetup.MetricData | null): void {
		this.changeMetrics(metric?.key ? [metric] : []);
	}

	changeMetrics(metrics: DSSetup.MetricData[]): void {
		this.metrics = metrics;
	}

	createCopy(): MetricLimitations {
		return new MetricLimitations({
			metrics: copyObject(this.metrics),
			isNumber: this.isNumber,
			dateRange: this.dateRange?.createCopy(),
			compareCMs: this.compareCMs.map((c) => c.createCopy()),
			manualLimitations: this.manualLimitations,
			rawLimitations: this.rawLimitations,
		});
	}

	getGranularityConstraints(dateRangeTypeId: string) {
		return MetricLimitations.granularityConstraints[dateRangeTypeId] || [];
	}

	// used to check if limitations have been loaded
	@tracked limitationsLoaded: Promise<void> | null = null;

	// used to check for base limitation and if validity checks are triggered
	@tracked private limitationsInitialized = false;

	private _setupLimitationsDataAndObservers(): void {
		if (this.limitationsInitialized) {
			return;
		}

		this.checkValidityForAll();

		next(() => {
			// to avoid get & set in the same runloop
			this.limitationsInitialized = true;
		});
	}

	static granularityConstraints: Record<string, GranulationTypeId[]> = {}; // by date range type id

	static createFromDataSelect(dsCM: DataSelect): MetricLimitations {
		const dateRange = DateRange.createFromFormula(dsCM.dateRange);
		const compareCMs = dsCM.compares.map((compareFormula) =>
			DsCompare.createFromFormula(compareFormula, dateRange, dsCM.metricDefaults),
		);

		if (!compareCMs.length) {
			compareCMs.push(DsCompare.createFromFormula(null, dateRange));
		}

		const limitations = new MetricLimitations({
			metrics: dsCM.metricData ? [dsCM.metricData] : [],
			isNumber: dateRange.granulationIsWholeRange,
			dateRange,
			compareCMs,
		});

		return limitations;
	}

	// the base constraints: the granulations each date range type allows
	static parseBaseConstraints(
		baseConstraints: Coredata.Constants['cdrLimitationsBase']['ranges'],
	): void {
		const granularityConstraints: Record<string, GranulationTypeId[]> = {};

		Object.keys(baseConstraints).forEach((dateRangeTypeId: string) => {
			const granulations = baseConstraints[dateRangeTypeId]!
				.granulations as GranulationTypeId[];
			granularityConstraints[dateRangeTypeId] = granulations;
		});

		this.granularityConstraints = granularityConstraints;
	}
}
